<template>
  <div class="min-h-screen bg-[#f5f7fa] py-6 px-4">
    <div class="max-w-[900px] mx-auto">
      <label for="hp" class="sr-only" aria-hidden="true">Leave this field blank</label>
      <input
        v-model="hp"
        type="text"
        class="hp"
        id="hp"
        name="hp"
        autocomplete="off"
        autocorrect="off"
        autocapitalize="off"
        spellcheck="false"
        tabindex="-1"
        aria-hidden="true"
        inert
        aria-label="Leave this field blank"
      />

      <!-- ═══ Wizard Card ═══════════════════════════════════════════════ -->
      <div class="bg-white rounded-2xl wizard-card">

        <!-- ─── Step Indicator Header ──────────────────────────────── -->
        <div class="wizard-header px-6 pt-6 pb-5 border-b border-gray-100 bg-white" role="group" aria-label="Enrollment progress">
          <div class="relative">
            <!-- Track line -->
            <div class="absolute top-[14px] left-[14px] right-[14px] h-[2px] bg-gray-200 rounded-full z-0"></div>
            <!-- Filled progress -->
            <div
              class="absolute top-[14px] left-[14px] h-[2px] bg-[#ce4c29] rounded-full z-0 transition-all duration-500"
              :style="{ width: progressLineWidth }"
            ></div>
            <!-- Step nodes -->
            <div class="relative z-10 flex justify-between">
              <div v-for="(label, idx) in stepLabels" :key="idx" class="flex flex-col items-center gap-2">
                <div
                  :class="[
                    'w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold border-2 transition-all duration-300 select-none',
                    currentStep > idx + 1
                      ? 'bg-[#b23f22] border-[#b23f22] text-white shadow-sm'
                      : currentStep === idx + 1
                      ? 'bg-[#b23f22] border-[#b23f22] text-white scale-110 shadow-md step-active'
                      : 'bg-white border-gray-400 text-gray-700'
                  ]"
                >
                  <svg v-if="currentStep > idx + 1" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3" aria-hidden="true" focusable="false">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  <span v-else>{{ idx + 1 }}</span>
                </div>
                <span :class="[
                  'text-[11px] font-medium text-center leading-tight hidden sm:block max-w-[72px] transition-colors',
                  currentStep === idx + 1 ? 'text-[#8f2e14] font-semibold' :
                  currentStep > idx + 1  ? 'text-gray-700' : 'text-gray-700'
                ]">{{ label }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- ─── Step Content ────────────────────────────────────────── -->
        <section class="p-6 lg:p-8 min-h-[280px] wizard-step-content" aria-label="Enrollment step content">

          <!-- Step 1: Questions / Union Info -->
          <div v-if="stepMap.license && currentStep === stepMap.license">
            <LicenseInfoSection
              :state="state"
              @reloadPlan="reloadPlan"
              @updateState="updateState"
            />
          </div>

          <!-- Step: Address -->
          <div v-show="currentStep === stepMap.address">
            <AddressInfoSection
              :key="`address-${resetCounter}`"
              ref="addressSection"
              v-if="searchObject.opt != '' && (state.opt_type !== 'SEIU721' || (state.isSEIU === 'yes' && state.isFAM === 'yes'))"
              :disabled="false"
              @getEffDate="getEffDate" @getPlan="getPlan" @updateState="updateState"
              :state="state" @resetForm="resetForm" :searchObject="searchObject"
            />
          </div>

          <!-- Step: Select Plan -->
          <div v-show="currentStep === stepMap.plan">
            <PlanSelectionSection
              v-if="state.disp_enroll_section"
              @setChoice="setChoice"
              @updateState="updateState"
              @studentBillingPeriodChange="handleStudentBillingPeriodChange"
              :state="state"
              @getPlanListAgain="reloadPlans"
            />
            <div v-if="!state.disp_enroll_section" class="py-16 text-center text-gray-400 text-sm">
              Complete the address step to view available plans.
            </div>
          </div>

          <!-- Step: Your Info -->
          <div v-show="currentStep === stepMap.user">
            <UserInfoSection
              :key="`user-${resetCounter}`"
              v-if="state.disp_enroll_section"
              :state="state" :user_form="user_form" :validation="v$" @update:user_form="updateField"
            />
          </div>

          <!-- Step: Payment -->
          <div v-show="showPaymentStep && currentStep === stepMap.payment">
            <PaymentInfoSection
              :key="`payment-${resetCounter}`"
              v-if="state.disp_enroll_section"
              :state="state" :user_form="user_form" @updateState="updateState" @getEffDate="getEffDate"
              :validation="v$" @update:user_form="updateField"
            />
          </div>

          <!-- Step: Review & Sign -->
          <div v-show="currentStep === stepMap.signature">
            <SignatureComp
              :key="`signature-${resetCounter}`"
              ref="signatureComp"
              v-if="state.disp_enroll_section"
              :state="state" :user_form="user_form" :validation="v$" @update:user_form="updateField"
              @createMember="createMember" @resetForm="resetForm" @reloadPlan="reloadPlan"
            />
          </div>

        </section>

      </div>
      <!-- ═══ End Wizard Card ═══════════════════════════════════════════ -->

      <!-- Sidebar below wizard (plan info — shown only after a plan is selected) -->
      <div v-if="currentStep === stepMap.plan && state.disp_enroll_section && state.bundled_group.some(g => g.select_plan)" class="mt-6 bg-white rounded-2xl p-6 sidebar-card">
        <SidebarInfo :state="state" @reloadPlan="reloadPlan" @updateState="updateState" />
      </div>

      <!-- ─── Sticky Navigation Footer ─────────────────────────────── -->
      <nav class="sticky bottom-4 z-30 mt-6" aria-label="Enrollment navigation">
        <div class="bg-white rounded-xl border border-gray-200 shadow-lg px-5 py-3 grid grid-cols-3 items-center gap-4">
          <!-- Back -->
          <div class="flex items-center justify-start">
            <button
              v-if="currentStep >= 1"
              @click="handleBack"
              class="primary-outline-button back-contrast flex items-center gap-2 !text-sm"
            >
              <svg
                v-if="currentStep > 1"
                class="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2.5"
                aria-hidden="true"
                focusable="false"
              >
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
              {{ currentStep === 1 ? 'Reset' : 'Back' }}
            </button>
          </div>

          <!-- Step counter -->
          <span class="text-xs text-gray-700 font-medium select-none text-center">Step {{ currentStep }} of {{ dynamicTotalSteps }}</span>

          <!-- Next / Submit -->
          <div class="flex items-center justify-end">
            <button
              v-if="currentStep < dynamicTotalSteps"
              @click="handleNext"
              class="primary-button flex items-center gap-2 !text-sm"
            >
              {{ currentStep === stepMap.plan ? 'Proceed To Checkout' : 'Next' }}
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5" aria-hidden="true" focusable="false">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>

            <button
              v-if="currentStep === dynamicTotalSteps"
              @click="$refs.signatureComp?.submitForm()"
              :disabled="!!submitBlockReason || isSubmitting"
              class="primary-button !text-sm disabled:opacity-40 disabled:cursor-not-allowed"
              :title="submitBlockReason || ''"
            >
              {{ isSubmitting ? 'Submitting...' : 'Purchase Plan' }}
            </button>
          </div>
        </div>
        <p
          v-if="currentStep === dynamicTotalSteps && submitBlockReason && !isSubmitting"
          class="mt-2 text-xs text-error text-center bg-white/90 rounded-md px-3 py-2 border border-red-100"
        >
          {{ submitBlockReason }}
        </p>
      </nav>

    </div>
  </div>
</template>
<style>
.hp { position: absolute; left: -9999px; }
.sr-only {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

.wizard-card {
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.07);
  border: 1px solid #eef0f4;
  overflow: visible;
}

.wizard-step-content {
  overflow: visible;
}
.sidebar-card {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
  border: 1px solid #eef0f4;
}
.wizard-header {
  position: sticky;
  top: 0;
  z-index: 20;
}
.step-active {
  box-shadow: 0 0 0 4px rgba(206, 76, 41, 0.18), 0 2px 8px rgba(206, 76, 41, 0.3);
}

/* WCAG: increase contrast for Back button text/border only */
.back-contrast {
  color: #b23f22 !important;
  border-color: #b23f22 !important;
}
</style>
<script>
import useVuelidate from "@vuelidate/core";
import { email, required, helpers } from "@vuelidate/validators";
import { reactive } from "vue";
import Swal from "sweetalert2";
import { computed, watch } from "vue";
import moment from "moment";
import LicenseInfoSection from "../components/LicenseInfoSection.vue";
import AddressInfoSection from "../components/AddressInfoSection.vue";
import PlanSelectionSection from "../components/PlanSelectionSection.vue";
import UserInfoSection from "../components/UserInfoSection.vue";
import PaymentInfoSection from "../components/PaymentInfoSection.vue";
import SidebarInfo from "../components/SidebarInfo.vue";
import SignatureComp from "../components/SignatureComp.vue";
import { useStore } from "vuex"; // Import the `useStore` hook from Vuex
import filter from "lodash/filter";
import {
  isStudentFlow as checkStudentFlow,
  effectiveOptType as getEffectiveOptType,
  isStudentPlanGroup as checkStudentPlanGroup,
  shouldUseStudentPricing as checkShouldUseStudentPricing,
  usesStudentPlanPricing as checkUsesStudentPlanPricing,
  usesMixedStudentBundlePricing as checkUsesMixedStudentBundlePricing,
  getGeneralNextRecurringDate,
  getStudentEnrollmentDate,
  getStudentEffectiveDate,
  getStudentNextRecurringDate,
  applyStudentCampaignFilter as filterStudentCampaign,
  filterPlansByQuestions as filterPlansByEnrollmentQuestions,
  canProceedFromQuestions,
  shouldApplyQuestionPlanFilter as checkShouldApplyQuestionPlanFilter,
  usesStudentParameter as checkUsesStudentParameter,
  shouldSkipProrated as checkShouldSkipProrated,
  sortStudentPlanDisplayOrder,
  resolveServiceFeeAmount,
  formatPlanPriceDisplay,
} from "@/utils/enrollmentFlow";
import {
  pushGeneralEnrollmentEvent,
  pushEnrollmentSuccessEvent,
  GENERAL_ENROLLMENT_EVENTS,
  isGeneralEnrollmentTrackingEnabled,
} from "@/utils/generalEnrollmentAnalytics";

// SweetAlert2 accessibility + WCAG contrast normalization (metadata-only).
// - Ensures confirm button has aria-label (WAVE "missing form label")
// - Ensures icons have aria-label (WAVE "missing alternative text")
// - Darkens warning icon to meet contrast
const swal = Swal.mixin({
  didOpen: (popup) => {
    const ensureAriaLabel = (btn, fallback) => {
      if (!btn) return;
      const label = (btn.textContent || fallback || "").trim() || fallback || "OK";
      if (!btn.getAttribute("aria-label")) {
        btn.setAttribute("aria-label", label);
      }
    };

    // Label all SweetAlert action buttons (WAVE may flag any unlabeled control).
    ensureAriaLabel(popup?.querySelector?.("button.swal2-confirm"), "OK");
    ensureAriaLabel(popup?.querySelector?.("button.swal2-cancel"), "Cancel");
    ensureAriaLabel(popup?.querySelector?.("button.swal2-deny"), "Deny");
    ensureAriaLabel(popup?.querySelector?.("button.swal2-close"), "Close");

    // Ensure dialog is associated to its title when present.
    const titleEl = popup?.querySelector?.(".swal2-title");
    if (titleEl) {
      if (!titleEl.id) titleEl.id = "swal2-title";
      const container = popup?.closest?.(".swal2-popup");
      if (container && !container.getAttribute("aria-labelledby")) {
        container.setAttribute("aria-labelledby", titleEl.id);
      }
    }

    // SweetAlert2 includes hidden inputs by default; hide them from a11y tools.
    // (WAVE flags them as "missing form label" even when display:none)
    const hiddenControls = popup?.querySelectorAll?.("#swal2-input, input.swal2-input, input.swal2-file") || [];
    hiddenControls.forEach((el) => {
      el.setAttribute("aria-hidden", "true");
      el.setAttribute("tabindex", "-1");
      el.setAttribute("disabled", "true");
    });

    const icon = popup?.querySelector?.(".swal2-icon");
    if (icon) {
      icon.setAttribute("aria-hidden", "true");
      icon.removeAttribute("aria-label");
    }

    // Improve warning icon contrast (WAVE very low contrast on icon).
    const warnIcon = popup?.querySelector?.(".swal2-icon.swal2-warning");
    if (warnIcon) {
      warnIcon.style.borderColor = "#b23f22";
      warnIcon.style.color = "#b23f22";
    }
  },
});
const futureDate = helpers.withMessage(
  "Enter a valid date (MM/DD/YYYY or YYYY-MM-DD) between 1900 and today",
  (value) => {
    if (!value) return false;

    let year, month, day;

    // Detect format and extract year, month, and day
    if (value.includes("/")) {
      // Format: MM/DD/YYYY
      const parts = value.split("/");
      if (parts.length !== 3) return false;
      [month, day, year] = parts.map(Number);
    } else if (value.includes("-")) {
      // Format: YYYY-MM-DD
      const parts = value.split("-");
      if (parts.length !== 3) return false;
      [year, month, day] = parts.map(Number);
    } else {
      return false; // Invalid format
    }

    if (!isValidDate(year, month, day)) return false;

    const selectedDate = new Date(year, month - 1, day);
    const today = new Date();

    return selectedDate <= today && year >= 1900 && year <= today.getFullYear();
  }
);

// Function to check if a given year, month, and day form a valid date
function isValidDate(year, month, day) {
  if (isNaN(year) || isNaN(month) || isNaN(day)) return false;
  if (year < 1 || month < 1 || month > 12 || day < 1) return false;

  const maxDays = new Date(year, month, 0).getDate(); // Get last day of the month
  return day <= maxDays;
}

const validateExpiryMonth = helpers.withMessage(
  "Enter a valid expiry month (MM/YYYY or YYYY-MM) that is today or in the future",
  (value) => {
    if (!value) return false;

    let month, year;

    // Detect format and extract month and year
    if (value.includes("/")) {
      // Format: MM/YYYY
      const parts = value.split("/");
      if (parts.length !== 2) return false;
      [month, year] = parts.map(Number);
    } else if (value.includes("-")) {
      // Format: YYYY-MM
      const parts = value.split("-");
      if (parts.length !== 2) return false;
      [year, month] = parts.map(Number);
    } else {
      return false; // Invalid format
    }

    if (!isValidMonth(year, month)) return false;

    const selectedDate = new Date(year, month - 1, 1); // First day of the selected month
    const today = new Date();
    const currentMonth = new Date(today.getFullYear(), today.getMonth(), 1); // First day of the current month

    return selectedDate >= currentMonth; // Expiry month should be today or in the future
  }
);

// Function to check if the month and year are valid
function isValidMonth(year, month) {
  if (isNaN(year) || isNaN(month)) return false;
  if (year < 1900 || year > 2099) return false; // Set a reasonable upper limit
  return month >= 1 && month <= 12;
}


export default {
  name: "MemberEnrollmentForm",
  setup() {
    const store = useStore();
    const state = computed(() => store.state);

    const user_form = reactive({ ...state.value.member });
    watch(
      () => state.value.member, // The source to watch
      (newMember) => {
        // Update user_form with the new values
        Object.assign(user_form, newMember);
      },
      { deep: true } // Watch deeply for nested changes
    );

    const updateState = (value) => {
      store.dispatch("updateState", { key: value.key, value: value.value });
    };
    const updateField = (updatedForm) => {
      Object.assign(user_form, updatedForm);
      Object.entries(user_form).map(([field, f_value]) => {
        updateState({ key: "member." + field, value: f_value });
      });
    };
    const isStudentFlow = computed(() => checkStudentFlow(state.value));
    const effectiveOptType = computed(() => getEffectiveOptType(state.value));

    const isCreditCardRequired = computed(() => {
      return state.value.payment_method != "Payroll" && state.value.enroll_option != "affiliated";
    });

    // ── Wizard step helpers ──────────────────────────────────────────
    const hasStep1 = computed(() =>
      state.value.opt_type === 'general' ||
      state.value.opt_type === 'SEIU721'
    );

    const showPaymentStep = computed(() => true);

    const dynamicTotalSteps = computed(() => {
      const base = hasStep1.value ? 5 : 4;
      return showPaymentStep.value ? base + 1 : base;
    });

    const stepMap = computed(() => {
      const s1 = hasStep1.value;
      const hp = showPaymentStep.value;
      if (s1) {
        return { license: 1, address: 2, plan: 3, user: 4, payment: hp ? 5 : null, signature: hp ? 6 : 5 };
      }
      return { license: null, address: 1, plan: 2, user: 3, payment: hp ? 4 : null, signature: hp ? 5 : 4 };
    });

    const stepLabels = computed(() => {
      const labels = [];
      if (hasStep1.value) {
        labels.push(state.value.opt_type === 'general' ? 'Questions' : 'Union Info');
      }
      labels.push('Address', 'Select Plan', isStudentFlow.value ? "Student's Information" : 'Your Info');
      if (showPaymentStep.value) labels.push('Payment');
      labels.push('Review & Sign');
      return labels;
    });

    const requiresOccupation = computed(
      () =>
        (state.value.opt_type === "direct_page" &&
          state.value.company_number === "9001204") ||
        state.value.opt_type == "mdsid"
    );

    const requiresGender = computed(() => state.value.opt_type == "mdsid");

    const rules = computed(() => ({
      user_form: {
        first_name: { required },
        // spouse_name: state.value.payment_method == 'Payroll' && state.value.payroll_enrollment == '1' ? { required } : {},
        last_name: { required },
        email: { required, email },
        phone: { required },
        occupation: requiresOccupation.value ? { required } : {},
        gender: requiresGender.value ? { required } : {},
        // other_occupation: (user_form.occupation === 'other' && state.value.company_number === '9001204') || (user_form.occupation === 'other' && state.value.opt_type == 'mdsid') ? { required } : {},
        ssn: state.value.ssn_required == 1 || state.value.ssn_required == '1' ? { required } : {},
        date_of_birth: { required, futureDate },
        driv_lic_state: (state.value.cdl_plan && state.value.opt_type != 'ccwsafe') ? { required } : {},
        driv_license: (state.value.cdl_plan && state.value.opt_type != 'ccwsafe') ? { required } : {},
        employee_id: !(state.value.employee_id_required != '1' || state.value.enroll_option == 'individual') ? { required } : {},
        creditCardHolder: isCreditCardRequired.value ? { required } : {},
        card_holder_phone:
          isCreditCardRequired.value && isStudentFlow.value
            ? { required }
            : {},
        card_holder_email:
          isCreditCardRequired.value && isStudentFlow.value
            ? { required, email }
            : {},
        cc_type: isCreditCardRequired.value ? { required } : {},
        cc: isCreditCardRequired.value ? { required } : {},
        exp_month: isCreditCardRequired.value ? { required, validateExpiryMonth } : {},
        ccv: isCreditCardRequired.value ? { required } : {},
        billingZip: isCreditCardRequired.value ? { required } : {},
        eSignature: { required },
        iagree: { required },
        mdsId: state.value.opt_type == 'mdsid' ? { required } : {},
        seaName: state.value.opt_type == 'mdsid' ? {} : {},
        seaNumber: state.value.opt_type == 'mdsid' ? {} : {},
        seiuLocalName: state.value.opt_type == 'mdsid' ? {} : {},
        seiuLocalNumber: state.value.opt_type == 'mdsid' ? {} : {},
        school_name:
          isStudentFlow.value
            ? { required }
            : {},
      },
    }));

    const v$ = useVuelidate(rules, { user_form });

    return {
      user_form,
      updateField,
      state,
      updateState,
      v$,
      hasStep1,
      showPaymentStep,
      stepMap,
      dynamicTotalSteps,
      stepLabels,
      requiresOccupation,
      requiresGender,
      isStudentFlow,
      effectiveOptType,
    };
  },
  data() {
    return {
      hp: '',
      startTime: Date.now(),
      siteKey: 'YOUR_SITE_KEY',
      searchObject: {},
      currentStep: 1,
      isSubmitting: false,
      resetCounter: 0,
      suppressEnrollmentStepTracking: false,
    };
  },
  watch: {
    currentStep(newStep) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      if (!this.suppressEnrollmentStepTracking) {
        this.trackGeneralEnrollmentStep(newStep);
      }
    }
  },
  computed: {
    progressLineWidth() {
      return this.dynamicTotalSteps > 1
        ? `${((this.currentStep - 1) / (this.dynamicTotalSteps - 1)) * 100}%`
        : '0%';
    },
    submitBlockReason() {
      if (this.currentStep !== this.dynamicTotalSteps) return "";
      if (this.isSubmitting) return "";
      if (this.user_form.iagree !== "I AGREE") {
        return "Please confirm the terms checkbox before purchasing.";
      }
      if (this.v$.user_form?.eSignature?.$invalid) {
        return "Please enter your electronic signature before purchasing.";
      }
      if (this.v$.user_form?.$invalid) {
        return "Please complete all required fields before purchasing.";
      }
      return "";
    },
  },
  components: {
    LicenseInfoSection,
    AddressInfoSection,
    PlanSelectionSection,
    UserInfoSection,
    PaymentInfoSection,
    SidebarInfo,
    SignatureComp,
  },
  mounted() {
    this.searchObject = this.$route.query;
    if (this.searchObject) {
      if (this.searchObject.opt != undefined && this.searchObject.opt != "") {
        if (this.searchObject.opt == "general") {
          this.updateState({ key: "opt_type", value: "general" });
          this.updateState({ key: "enroll_option", value: "individual" });
        } else if (this.searchObject.opt == "ccwsafe") {
          this.updateState({ key: "opt_type", value: "ccwsafe" });
          this.updateState({ key: "enroll_option", value: "individual" });
        } else if (this.searchObject.opt.toLowerCase() == "seiu721") {
          this.searchObject.opt = "SEIU721";
          this.updateState({ key: "opt_type", value: "SEIU721" });
        } else if (this.searchObject.opt == "mdsid") {
          this.updateState({ key: "member.mdsId", value: this.searchObject.value });
          this.updateState({ key: "opt_type", value: "mdsid" });
          this.updateState({
            key: "opt_value",
            value: this.searchObject.value,
          });
        } else if (this.searchObject.opt.toLowerCase() === "student") {
          this.updateState({ key: "opt_type", value: "student" });
          this.updateState({ key: "enroll_option", value: "individual" });
        } else {
          // this.updateState({ key: "enroll_option", value: "affilliated" });
          this.updateState({ key: "opt_type", value: "direct_page" });
        }
      } else {
        swal.fire({
          title: "We're Sorry!",
          text: "Invalid company. Please contact Member Care at (800) 356-LAWS.",
          icon: "error",
          confirmButtonText: "OK",
          confirmButtonAriaLabel: "OK",
        });
        window.open("https://www.uslegalservices.net");
      }
      var date = new Date();
      var lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      console.log(lastDay, 'lastDay')
      let last_day_month = new Date(lastDay).toLocaleDateString("en-US", {
        day: "2-digit",
      });
      console.log(last_day_month, 'last_day_month')

      this.updateState({ key: "last_day_month", value: last_day_month });

      const next_month = moment().add(1, "months");
      this.updateState({ key: "next_month", value: new Date(next_month) });
      this.getToken();
      if (this.searchObject.opt === "general") {
        this.trackGeneralEnrollment(GENERAL_ENROLLMENT_EVENTS.FORM_OPEN, {
          step_number: 1,
          step_name: this.getStepTrackingName(1),
        });
      }
    } else {
      console.error("Route parameter 'id' is missing.");
    }
  },
  methods: {
    trackGeneralEnrollment(event, extra = {}) {
      if (!isGeneralEnrollmentTrackingEnabled() || this.state.opt_type !== "general") return;
      pushGeneralEnrollmentEvent(event, this.state, extra);
    },
    getStepTrackingName(stepNumber) {
      const map = this.stepMap;
      if (map.license && stepNumber === map.license) return "questions";
      if (stepNumber === map.address) return "address";
      if (stepNumber === map.plan) return "select_plan";
      if (stepNumber === map.user) return "your_info";
      if (this.showPaymentStep && stepNumber === map.payment) return "payment";
      if (stepNumber === map.signature) return "review_sign";
      return `step_${stepNumber}`;
    },
    trackGeneralEnrollmentStep(stepNumber) {
      this.trackGeneralEnrollment(GENERAL_ENROLLMENT_EVENTS.STEP_VIEW, {
        step_number: stepNumber,
        step_name: this.getStepTrackingName(stepNumber),
        total_steps: this.dynamicTotalSteps,
      });
    },
      reloadPlans() {
    const payload = {
      state: this.state.member?.state || this.state.student_school_state,
      opt_type: this.effectiveOptType,
      enroll_option: this.state.enroll_option,
      is_list: false,
    };
    if (!this.isStudentFlow && this.state.company_number) {
      payload.company = this.state.company_number;
    }
    if (this.isStudentFlow) {
      payload.student_school_state = this.state.member?.state || this.state.student_school_state;
    }

    this.getPlan(payload);
  },
    getNeaMemberDetails(value) {
      this.$store
        .dispatch("getNeaMemberDetails", { mdsid: value, refurl: "refurl", "opt_type": this.state.opt_type })
        .then((responseData) => {
          if (responseData.status == "success") {
            Object.entries(responseData.data).map(([field, f_value]) => {
              console.log(field)
              this.user_form[field] = f_value;
              this.updateState({ key: "member." + field, value: f_value });
            });
          } else {
            //
          }
        });
    },
    async handleNext() {
      // Step 1 — Questions (general) / Union Info (SEIU721)
      if (this.stepMap.license && this.currentStep === this.stepMap.license) {
        if (this.state.opt_type === 'general') {
          if (!this.state.whoFor) {
            swal.fire({ title: 'Required', text: 'Please indicate who you are buying a plan for.', icon: 'warning', confirmButtonText: 'OK', confirmButtonAriaLabel: 'OK' });
            return;
          }
          if (this.state.whoFor !== 'Your student' && !this.state.hasCDL) {
            swal.fire({ title: 'Required', text: 'Please answer the CDL question before continuing.', icon: 'warning', confirmButtonText: 'OK', confirmButtonAriaLabel: 'OK' });
            return;
          }
          if (!canProceedFromQuestions(this.state)) {
            swal.fire({ title: 'Required', text: 'Please select which plans you are interested in.', icon: 'warning', confirmButtonText: 'OK', confirmButtonAriaLabel: 'OK' });
            return;
          }
          this.updateState({ key: 'plan_questions_complete', value: true });
          this.trackGeneralEnrollment(GENERAL_ENROLLMENT_EVENTS.QUESTIONS_COMPLETE, {
            enrollment_parameter: this.effectiveOptType,
          });
        } else if (this.state.opt_type === 'SEIU721') {
          if (this.state.isSEIU !== 'yes') {
            swal.fire({ title: 'Required', text: 'SEIU union membership is required to proceed.', icon: 'warning', confirmButtonText: 'OK', confirmButtonAriaLabel: 'OK' });
            return;
          }
          if (!this.state.company_number) {
            swal.fire({ title: 'Required', text: 'Please select your company before proceeding.', icon: 'warning', confirmButtonText: 'OK', confirmButtonAriaLabel: 'OK' });
            return;
          }
        }
        this.currentStep++;
        return;
      }

      // Step — Address (calls submitForm → triggers getPlan → advances to plan step)
      if (this.currentStep === this.stepMap.address) {
        this.$refs.addressSection?.submitForm();
        return;
      }

      // Step — Plan selection
      if (this.currentStep === this.stepMap.plan) {
        const selected = this.state.bundled_group.filter(p => p.select_plan);
        if (!selected.length) {
          swal.fire({ title: 'Required', text: 'Please select a plan before continuing.', icon: 'warning', confirmButtonText: 'OK', confirmButtonAriaLabel: 'OK' });
          return;
        }
        this.updateState({ key: 'checkout_started', value: true });
        this.trackGeneralEnrollment(GENERAL_ENROLLMENT_EVENTS.PLAN_SELECTED, {
          plan_count: selected.length,
          plan_names: selected.map((p) => p.description).join(" | "),
        });
        this.trackGeneralEnrollment(GENERAL_ENROLLMENT_EVENTS.CHECKOUT_STARTED);
        this.currentStep++;
        return;
      }

      // Step — Your Info
      if (this.currentStep === this.stepMap.user) {
        const fields = ['first_name', 'last_name', 'email', 'phone', 'date_of_birth'];
        if (this.state.ssn_required == '1') fields.push('ssn');
        if (!(this.state.employee_id_required != '1' || this.state.enroll_option == 'individual')) fields.push('employee_id');
        if (this.requiresOccupation) fields.push('occupation');
        if (this.requiresGender) fields.push('gender');
        if (this.state.cdl_plan && this.state.opt_type != 'ccwsafe') fields.push('driv_lic_state', 'driv_license');
        if (this.isStudentFlow) fields.push('school_name');
        fields.forEach(f => this.v$.user_form[f]?.$touch());
        if (fields.some(f => this.v$.user_form[f]?.$invalid)) {
          swal.fire({ title: 'Required Fields', text: 'Please complete all required fields before continuing.', icon: 'warning', confirmButtonText: 'OK', confirmButtonAriaLabel: 'OK' });
          return;
        }
        const memberType = this.state.payroll_enrollment == "1" ? "enrollment" : "member";
        const liveEmail = (this.user_form.email || this.state.member.email || "").trim();
        const liveSsn = (this.user_form.ssn || this.state.member.ssn || "").trim();
        const validationPayload = {
          email: liveEmail,
          ssn: liveSsn,
          // Keep company_id source aligned with submit flow.
          company_id:
            this.state.company?.company_id ??
            this.state.company?.id ??
            this.state.company?.number ??
            this.state.company_number ??
            this.searchObject?.opt,
          employee_id: this.user_form.employee_id || this.state.member.employee_id || "",
          payment_method: this.state.payment_method,
          payroll_enrollment: this.state.payroll_enrollment,
          enroll_option: this.state.enroll_option,
          opt_type: this.effectiveOptType,
          memberType,
        };
        const validationResponse = await this.$store.dispatch(
          "validateMemberDetails",
          validationPayload
        );
        if (
          !validationResponse ||
          validationResponse?.data?.status !== "success"
        ) {
          swal.fire({
            title: "We're Sorry!",
            text:
              validationResponse?.data?.message ||
              "Unable to validate your information. Please review and try again.",
            icon: "error",
            confirmButtonText: "OK",
            confirmButtonAriaLabel: "OK",
          });
          return;
        }
        this.trackGeneralEnrollment(GENERAL_ENROLLMENT_EVENTS.MEMBER_VALIDATED);
        this.currentStep++;
        return;
      }

      // Step — Payment
      if (this.showPaymentStep && this.currentStep === this.stepMap.payment) {
        if (this.state.payment_method !== 'Payroll' && this.state.enroll_option !== 'affilliated') {
          const payFields = [
            'creditCardHolder',
            ...(this.isStudentFlow ? ['card_holder_phone', 'card_holder_email'] : []),
            'cc_type', 'cc', 'exp_month', 'ccv', 'billingZip',
          ];
          payFields.forEach(f => this.v$.user_form[f]?.$touch());
          if (payFields.some(f => this.v$.user_form[f]?.$invalid)) {
            swal.fire({ title: 'Required Fields', text: 'Please complete your payment details before continuing.', icon: 'warning', confirmButtonText: 'OK', confirmButtonAriaLabel: 'OK' });
            return;
          }
          if (
            this.state.payment_method === "CC" &&
            !this.isCardExpiryAfterSubscriptionStart(this.user_form.exp_month, this.state.eff_date)
          ) {
            swal.fire({
              title: "Invalid Card Expiration",
              text: "The credit card expiration date must be greater than the subscription start date.",
              icon: "warning",
              confirmButtonText: "OK",
              confirmButtonAriaLabel: "OK",
            });
            return;
          }
        }
        this.trackGeneralEnrollment(GENERAL_ENROLLMENT_EVENTS.PAYMENT_STEP_COMPLETE);
        this.currentStep++;
        return;
      }

      this.currentStep++;
    },
    handleBack() {
      if (this.currentStep === 1) {
        this.resetForm();
        return;
      }
      this.handlePrev();
    },
    handlePrev() {
      if (this.currentStep > 1) {
        // Leaving plan step going back — deselect all plans
        if (this.currentStep === this.stepMap.plan) {
          const b_group = this.state.bundled_group.map(p => ({ ...p, select_plan: false }));
          this.updateState({ key: 'bundled_group', value: b_group });
          this.updateState({ key: 'checkout_started', value: false });
          this.updateState({ key: 'show_payment', value: false });
          this.updateState({ key: 'payment_method', value: '' });
        }
        // Leaving address step going back to Step 1 — reset bundled_group so all plan interest options show
        if (this.stepMap.license && this.currentStep === this.stepMap.address) {
          this.updateState({ key: 'bundled_group', value: [] });
          this.updateState({ key: 'disp_enroll_section', value: false });
        }
        this.currentStep--;
      }
    },
    resetForm() {
      this.suppressEnrollmentStepTracking = true;
      this.isSubmitting = false;
      // Clear all member fields in the store first so watchEffect doesn't re-populate
      this.updateState({
        key: "member",
        value: {
          spouse_name: "", first_name: "", last_name: "", employee_id: "",
          creditCardHolder: "", card_holder_phone: "", card_holder_cell: "true", card_holder_email: "",
          cc_type: "visa", cc: "", exp_month: "", ccv: "",
          eSignature: "", iagree: "", occupation: "", other_occupation: "",
          bank_branch: "", billingZip: "",
          gender: "Male", mdsId: "",
          seaName: "", seaNumber: "", seiuLocalName: "", seiuLocalNumber: "",
          ssn: "", email: "", phone: "", cell: "true",
          address: null, address1: "", address2: "", city: "", state: "",
          county: "", zip: "", date_of_birth: null, driv_license: "",
          driv_lic_state: "", mi: "", work_phone: "", employer_name: "", school_name: ""
        }
      });
      // Trigger AddressInfoSection local form reset
      this.updateState({ key: "reset_form", value: true });
      // Clear local user_form reactive object
      Object.keys(this.user_form).forEach((key) => (this.user_form[key] = ""));
      this.user_form.cc_type = "visa";
      this.user_form.gender = "Male";
      this.user_form.cell = "true";
      this.user_form.card_holder_cell = "true";
      this.v$.$reset();
      this.currentStep = 1;
      this.$nextTick(() => {
        this.suppressEnrollmentStepTracking = false;
      });
      this.updateState({ key: "disp_enroll_section", value: false });
      this.updateState({ key: "comm_driv_lic", value: "Yes" });
      this.updateState({ key: "disp_flag", value: false });
      this.updateState({ key: "hasCDL", value: "" });
      this.updateState({ key: "planInterest", value: "" });
      this.updateState({ key: "enrollment_parameter", value: "" });
      this.updateState({ key: "whoFor", value: "" });
      this.updateState({ key: "isSEIU", value: this.state.opt_type === 'SEIU721' ? "" : "yes" });
      this.updateState({ key: "plan_questions_complete", value: false });
      this.updateState({ key: "student_school_state", value: "" });
      this.updateState({ key: "student_university_name", value: "" });
      this.updateState({ key: "student_billing_period", value: "monthly" });
      this.updateState({ key: "checkout_started", value: false });
      this.updateState({ key: "bundled_group", value: [] });
      this.updateState({ key: "payment_method", value: "" });
      this.updateState({ key: "show_payment", value: false });
      this.updateState({ key: "cdl_plan", value: false });
      this.updateState({ key: "eff_date", value: "" });
      this.updateState({ key: "next_recurring_date", value: "" });
      this.resetCounter += 1;
      setTimeout(() => {
        this.updateState({ key: "reset_form", value: false });
      }, 200);
    },
    deepTrim(obj) {
      if (!obj || typeof obj !== "object") return;

      Object.keys(obj).forEach((key) => {
        var val = obj[key];

        if (typeof val === "string") {
          obj[key] = val.trim();
        } else if (val && typeof val === "object") {
          this.deepTrim(val);
        }
      });
    },
    isCardExpiryAfterSubscriptionStart(expiryValue, subscriptionStart) {
      if (!expiryValue || !subscriptionStart) return true;

      let month;
      let year;
      const value = String(expiryValue).trim();

      if (value.includes("/")) {
        const parts = value.split("/");
        if (parts.length !== 2) return false;
        month = Number(parts[0]);
        year = Number(parts[1]);
      } else if (value.includes("-")) {
        const parts = value.split("-");
        if (parts.length !== 2) return false;
        year = Number(parts[0]);
        month = Number(parts[1]);
      } else {
        return false;
      }

      if (!Number.isFinite(month) || !Number.isFinite(year) || month < 1 || month > 12) {
        return false;
      }
      if (year < 100) {
        year += 2000;
      }

      const expiryEndOfMonth = new Date(year, month, 0, 23, 59, 59, 999);
      const startDate = new Date(subscriptionStart);
      if (Number.isNaN(startDate.getTime())) return true;

      return expiryEndOfMonth > startDate;
    },
    getPlanUnavailableMessage(fallback) {
      const plan = (this.state.planInterest || "").trim();
      const collegeDefenderInterestUrl =
        "https://hello.uslegalservices.net/college_defender_interest_form?utm_source=enrollment_form&utm_medium=enrollment_form&utm_campaign=college_defender";
      const collegeDefenderUnavailableMessage =
        `Enrollment for College Defender in your state is not available yet. Please <a href="${collegeDefenderInterestUrl}" target="_blank" rel="noopener noreferrer" style="color: #B23F22; text-decoration: underline; font-weight: 600;">CLICK HERE</a> to enter your information to be notified when your state becomes available.`;
      if (plan === "Both") {
        return "Student Defender & Identity Defender bundle plans are not available in your state yet.";
      }
      if (plan === "Student Defender" || (!plan && this.isStudentFlow)) {
        return collegeDefenderUnavailableMessage;
      }
      if (plan) {
        return `Enrollment for ${plan} in your state is not available yet.`;
      }
      return fallback || "Plans not available under this company state";
    },
    shouldApplyQuestionPlanFilter() {
      return checkShouldApplyQuestionPlanFilter(this.state);
    },
    getEffDate() {
      if (checkUsesStudentParameter(this.state)) {
        const enrollmentDate = getStudentEnrollmentDate();
        const effectiveDate = getStudentEffectiveDate(enrollmentDate);
        const nextRecurring = getStudentNextRecurringDate(
          enrollmentDate,
          this.state.student_billing_period
        );
        this.updateState({ key: "eff_date", value: effectiveDate });
        this.updateState({ key: "next_recurring_date", value: nextRecurring });
        this.updateState({ key: "day_diff", value: 0 });
        return;
      }
      if (this.state.payment_method != "CC") {
        // const plan = this.state.bundled_group.filter(
        //   (item) => item.select_plan === true
        // );

        const today = new Date().getDate();
        let now = new Date();
        let current;
        let { effective_date_cutoff, effective_date_after_enrollment } = this.state.enrollment_settings;

        let cutoff = parseInt(effective_date_cutoff, 10);
        let afterEnrollment = parseInt(effective_date_after_enrollment, 10);

        // If after enrollment is > 0, apply that rule directly
        if (!isNaN(afterEnrollment) && afterEnrollment > 0) {
          current = new Date();
          current.setDate(current.getDate() + afterEnrollment);
        } else {
          // Fallback to cutoff rule
          if (effective_date_cutoff !== '' && effective_date_cutoff != null) {
            if (today < cutoff) {
              now.setMonth(now.getMonth() + 1); // Move to next month
              current = new Date(now.getFullYear(), now.getMonth(), 1);
            } else {
              now.setMonth(now.getMonth() + 2); // Move to the month after next
              current = new Date(now.getFullYear(), now.getMonth(), 1);
            }
          } else {
            // Else rule
            if (today >= 24) {
              if (now.getMonth() === 11) {
                current = new Date(now.getFullYear() + 1, 0, 1);
              } else {
                current = new Date(now.getFullYear(), now.getMonth() + 2, 1);
              }
            } else {
              if (now.getMonth() === 11) {
                current = new Date(now.getFullYear() + 1, 0, 1);
              } else {
                current = new Date(now.getFullYear(), now.getMonth() + 1, 1);
              }
            }
          }
        }

        this.updateState({ key: "eff_date", value: current });
        // if (plan.length > 0 && plan[0].description === "CDL Defender") {
        //   const tomorrow = moment(new Date()).add(1, "days");
        //   this.updateState({ key: "eff_date", value: new Date(tomorrow) });
        // }
      } else {
        let startDate = new Date();
        const now = moment(startDate); // today's date
        console.log(now, 'now')
        const lastDay = moment(startDate).endOf("month"); // end of the month
        console.log(lastDay, 'lastDay')
        const duration = moment.duration(lastDay.diff(now));
        console.log(duration, 'duration')
        const days = duration.asDays() - 1;
        console.log(days, 'days');
        this.updateState({ key: "day_diff", value: Math.ceil(days) });

        const day = new Date(startDate);
        let date_eff = new Date(day);
        date_eff.setDate(day.getDate() + 1);
        this.updateState({ key: "eff_date", value: date_eff });
        const nextRecurring = getGeneralNextRecurringDate();
        this.updateState({ key: "next_recurring_date", value: nextRecurring });
        this.updateState({ key: "next_month", value: new Date(nextRecurring) });
      }
    },
    getPlan(payload) {
      payload.opt_type = this.effectiveOptType;
      this.$store.dispatch("getPlan", payload).then((responseData) => {
        console.log(responseData);
        if (responseData.status === "Not found") {
          this.updateState({
            key: "disp_enroll_section",
            value: false,
          });
        swal.fire({
            title: "We're Sorry!",
            html: this.getPlanUnavailableMessage(responseData.error),
            icon: "error",
            confirmButtonText: "OK",
            confirmButtonAriaLabel: "OK",
          });
          return false;
        } else {
          if (responseData.company_status !== "Active") {
            this.updateState({
              key: "disp_enroll_section",
              value: false,
            });
            this.updateState({ key: "enroll_msg", value: true });
            return false;
          }
          this.updateState({
            key: "enrollment_settings.send_enrollment_pdf",
            value: responseData.send_enrollment_pdf,
          });
          this.updateState({ key: "enroll_msg", value: false });

          if (
            responseData.effective_date_cutoff !== null &&
            responseData.effective_date_cutoff !== ""
          ) {
            this.updateState({
              key: "enrollment_settings.effective_date_cutoff",
              value: responseData.effective_date_cutoff,
            });
          }
          if (
            responseData.effective_date_after_enrollment !== null &&
            responseData.effective_date_after_enrollment !== ""
          ) {
            this.updateState({
              key: "enrollment_settings.effective_date_after_enrollment",
              value: responseData.effective_date_after_enrollment,
            });
          }

          if (
            responseData.disclaimer_text !== null &&
            responseData.disclaimer_text !== ""
          ) {
            this.updateState({
              key: "enrollment_settings.disclaimer_text",
              value: responseData.disclaimer_text,
            });
          }

          if (
            responseData.e_sign_text !== null &&
            responseData.e_sign_text !== ""
          ) {
            this.updateState({
              key: "enrollment_settings.e_sign_text",
              value: responseData.e_sign_text,
            });
          }

          if (
            responseData.closed_enroll_period_message !== null &&
            responseData.closed_enroll_period_message !== ""
          ) {
            this.updateState({
              key: "enrollment_settings.closed_enroll_period_message",
              value: responseData.closed_enroll_period_message,
            });
          }

          if (
            responseData.open_enrollment_start_date !== null &&
            responseData.open_enrollment_start_date !== "" &&
            responseData.open_enrollment_end_date !== null &&
            responseData.open_enrollment_end_date !== ""
          ) {
            this.updateState({
              key: "enrollment_settings.open_enrollment_start_date",
              value: responseData.open_enrollment_start_date,
            });
            this.updateState({
              key: "enrollment_settings.open_enrollment_end_date",
              value: responseData.open_enrollment_end_date,
            });

            const today = new Date();
            const startDate = new Date(responseData.open_enrollment_start_date);
            const endDate = new Date(responseData.open_enrollment_end_date);
            if (today < startDate) {
              // Enrollment hasn't started yet
              this.updateState({
                key: "disp_enroll_section",
                value: false,
              });
              if (responseData.closed_enroll_period_message == null || responseData.closed_enroll_period_message == '') {
                responseData.closed_enroll_period_message = `This company's enrollment period has not started yet.`;
              }
              swal.fire({
                title: "We're Sorry!",
                text: responseData.closed_enroll_period_message,
                icon: "error",
                confirmButtonText: "OK",
                confirmButtonAriaLabel: "OK",
              });
              return false;
            } else if (today < startDate || today > endDate) {
              // Enrollment period is over
              this.updateState({
                key: "disp_enroll_section",
                value: false,
              });
              if (responseData.closed_enroll_period_message == null || responseData.closed_enroll_period_message == '') {
                responseData.closed_enroll_period_message = `This company's enrollment period is over.`;
              }
              swal.fire({
                title: "We're Sorry!",
                text: responseData.closed_enroll_period_message,
                icon: "error",
                confirmButtonText: "OK",
                confirmButtonAriaLabel: "OK",
              });
              return false;
            }

          }
          console.log(payload.is_list)
          if (payload.is_list) {
            console.log(responseData)
            this.updateState({ key: "companies", value: [responseData] });
            let company = [responseData];
            console.log(company)
            if (company.length > 0) {
              this.updateState({ key: "company", value: company[0] });
            } else {
              console.log("No companies found after filtering");
            }
          } else if (responseData.company_name) {
            this.updateState({ key: "company", value: responseData });
          }
          const allCampaignEmpty = responseData.bundled_group.every(g =>
            (!g.campaign || g.campaign.trim() === "") &&
            (!g.bundle_campaign || g.bundle_campaign.trim() === "")
          );

          if (allCampaignEmpty) {
            if (this.effectiveOptType === "student") {
              responseData.bundled_group = filterStudentCampaign(
                responseData.bundled_group,
                this.state
              );
            }
            if (this.state.opt_type === "general" && this.shouldApplyQuestionPlanFilter()) {
              responseData.bundled_group = filterPlansByEnrollmentQuestions(
                responseData.bundled_group,
                this.state
              );
            }

            if (this.state.opt_type == "ccwsafe") {
              responseData.bundled_group = responseData.bundled_group.filter(
                (group) => group.planType != "CDL"
              );
            }
          } else {
            const normalize = (str) => (str || "").toString().trim().toLowerCase();

            if (this.state.opt_type === "student") {
              responseData.bundled_group = responseData.bundled_group.filter((group) => {
                return (
                  normalize(group.campaign).includes("student") ||
                  normalize(group.bundle_campaign).includes("student")
                );
              });
            } else if (this.state.opt_type === "general") {
              if (this.effectiveOptType === "student") {
                responseData.bundled_group = filterStudentCampaign(
                  responseData.bundled_group,
                  this.state
                );
              } else {
                responseData.bundled_group = responseData.bundled_group.filter((group) => {
                  return (
                    normalize(group.campaign).includes("general") ||
                    normalize(group.bundle_campaign).includes("general")
                  );
                });
              }
              if (this.shouldApplyQuestionPlanFilter()) {
                responseData.bundled_group = filterPlansByEnrollmentQuestions(
                  responseData.bundled_group,
                  this.state
                );
              }
            }

            if (this.state.opt_type === "ccwsafe") {
              responseData.bundled_group = responseData.bundled_group.filter((group) => {
                return (
                  normalize(group.campaign).includes("ccwsafe") ||
                  normalize(group.bundle_campaign).includes("ccwsafe")
                );
              });
            }
          }

          if (checkShouldUseStudentPricing(this.state)) {
            responseData.bundled_group = sortStudentPlanDisplayOrder(
              responseData.bundled_group,
              this.state
            );
          }

          if (responseData.bundled_group.length == 0) {
            this.updateState({
              key: "disp_enroll_section",
              value: false,
            });
            swal.fire({
              title: "We're Sorry!",
              html: this.getPlanUnavailableMessage(),
              icon: this.isStudentFlow ? "error" : "warning",
              confirmButtonText: "OK",
              confirmButtonAriaLabel: "OK",
            });
          } else {
            this.updateState({
              key: "disp_enroll_section",
              value: true,
            });
            this.trackGeneralEnrollment(GENERAL_ENROLLMENT_EVENTS.GET_PLAN, {
              plan_count: responseData.bundled_group.length,
              state: payload.state || this.state.member?.state || "",
            });
            this.updateState({
              key: "bundled_group",
              value: responseData.bundled_group,
            });
            this.updateState({
              key: "page_options",
              value: responseData.page_options,
            });
            this.updateState({
              key: "payment_options",
              value: responseData.payment_options,
            });
            this.updateState({
              key: "company_emails",
              value: responseData.email,
            });
            this.updateState({
              key: "ssn_required",
              value: responseData.ssn_required,
            });

            if (
              responseData.processing_fee !== "" &&
              responseData.processing_fee !== null
            ) {
              this.updateState({
                key: "processing_fee_base",
                value: parseFloat(responseData.processing_fee).toFixed(2),
              });
            }
            this.updateState({
              key: "processing_fee_exclude_campaign",
              value: responseData.processing_fee_exclude_campaign || "",
            });
            this.syncDisplayServiceFee();
            this.updateState({
              key: "employee_id_required",
              value: responseData.employee_id_required,
            });
            this.updateState({
              key: "payment_frequency",
              value: responseData.payment_frequency,
            });
            this.updateState({ key: "member.ssn", value: "" });
            this.updateState({ key: "member.employee_id", value: "" });
            this.updateState({
              key: "send_enrollment_pdf",
              value: responseData.send_enrollment_pdf,
            });
            // Advance wizard to plan selection step
            this.currentStep = this.stepMap.plan;
            if (checkShouldUseStudentPricing(this.state)) {
              this.updateState({ key: "student_billing_period", value: "monthly" });
            }
            if (checkUsesStudentParameter(this.state)) {
              this.getEffDate();
            }
          }

          if (this.state.page_options === "Yes" && this.state.opt_type == "direct_page") {
            this.updateState({ key: "disp_err_msg", value: true });
          }
          // Quill is initialized via watcher when user reaches the signature step
        }
        if (this.state.bundled_group.length > 0) {
          let b_group = this.state.bundled_group;
          b_group.forEach((plan) => {
            plan.select_plan = false;
          });
          this.updateState({ key: "bundled_group", value: b_group });
          this.checkPlan(this.state.payment_options);
          this.calculatePlanAmounts();
        }
      });
    },
    checkPlan(payment_options) {
      this.updateState({ key: "show_payment", value: false });
      this.updateState({ key: "show_cc_label", value: false });
      this.updateState({ key: "show_payroll_label", value: false });
      this.updateState({ key: "show_payroll_enrollment_label", value: false });

      console.log(payment_options);
      console.log(payment_options.length);

      if (payment_options && payment_options.length === 1) {
        if (payment_options[0] === "CC") {
          this.updateState({ key: "payment_method", value: "CC" });
          this.updateState({ key: "show_cc_label", value: true });
          this.getEffDate();
        }

        if (payment_options[0] === "PAYROLL") {
          this.updateState({ key: "payment_method", value: "Payroll" });
          this.updateState({ key: "payroll_enrollment", value: "1" });
          this.updateState({ key: "show_payroll_label", value: true });
          this.getEffDate();
        }

        if (payment_options[0] === "ACH") {
          this.updateState({ key: "show_payment", value: true });
          this.updateState({ key: "payment_method", value: "" });
        }

        if (payment_options[0] === "Payroll Enrollment File") {
          this.updateState({ key: "payment_method", value: "Payroll" });
          this.updateState({ key: "payroll_enrollment", value: "1" });
          this.updateState({
            key: "show_payroll_enrollment_label",
            value: true,
          });
          this.getEffDate();
        } else {
          this.updateState({ key: "payroll_enrollment", value: "0" });
          this.updateState({
            key: "show_payroll_enrollment_label",
            value: false,
          });
        }
      } else {
        this.updateState({ key: "show_payment", value: true });
        this.updateState({ key: "payment_method", value: "" });
      }
      const bundledGroup = this.filterBundledGroup({ select_plan: true });
      if (bundledGroup.length === 0) {
        this.updateState({ key: "cdl_plan", value: false });
      }
      bundledGroup.forEach((value) => {
        const plan_desc = value.description;
        if (plan_desc.match("CDL") && value.select_plan === true) {
          this.updateState({ key: "cdl_plan", value: true });
        } else {
          this.updateState({ key: "cdl_plan", value: false });
        }
      });
    },
    filterBundledGroup(criteria) {
      return this.state.bundled_group.filter((plan) => {
        return Object.keys(criteria).every(
          (key) => plan[key] === criteria[key]
        );
      });
    },
    async loadCompany() {
      const pageType =
        this.searchObject.opt !== "general" ? "dedicated" : "general";

      try {
        // Dispatching Vuex action to get the company list
        const response = await this.$store.dispatch("loadCompany", {
          page_type: pageType,
          opt_type: this.searchObject.opt,
          referer_url: "your-referer-url-here", // Replace with actual referer_url
        });

        if (response.data.status === "success") {
          let company = this.state.companies;
          console.log(company, 'company')
          if (this.searchObject.opt !== "mdsid") {
            company = company.filter(
              (c) => c.company.toLowerCase() === this.searchObject.opt.toLowerCase()
            );
          }

          if (company.length > 0) {
            console.log("Selected company:", company[0]);
            this.updateState({ key: "company", value: company[0] });
          } else {
            console.log("No companies found after filtering");
          }
        }
      } catch (error) {
        // console.error("Error loading company:", error);
        swal.fire({
          title: "We're Sorry!",
          text: error,
          icon: "error",
          confirmButtonText: "OK",
          confirmButtonAriaLabel: "OK",
        });
      }
    },
    async getToken() {
      // const refererUrl = "your-referer-url-here"; // Replace with actual value
      try {
        const response = await this.$store.dispatch("getToken", this.searchObject);
        // console.log(response);
        if (response.status === "Not found") {
          //
        } else {
          if (this.searchObject.opt !== "general") {
            this.loadCompany(); // Call loadCompany method
          }
          if (this.searchObject.opt == "mdsid") {
            this.getNeaMemberDetails(this.searchObject.value);
          }
        }
      } catch (error) {
        // console.error("Error logging in:", error);
        swal.fire({
          title: "We're Sorry!",
          text: error,
          icon: "error",
          confirmButtonText: "OK",
          confirmButtonAriaLabel: "OK",
        });
      }
    },
    checkDupes(array) {
      array.sort(); // Sort the array (ascending order)
      for (let i = 0; i < array.length - 1; i++) {
        if (array[i + 1] === array[i]) {
          return true;
        }
      }
      return false;
    },
    parseMultiValueField(value) {
      const raw = String(value ?? "").trim();
      if (!raw) return [];
      if (raw.includes(",")) {
        return raw.split(",").map((part) => part.trim()).filter(Boolean);
      }
      if (raw.includes(" + ")) {
        return raw.split(" + ").map((part) => part.trim()).filter(Boolean);
      }
      if (raw.includes("+")) {
        return raw.split("+").map((part) => part.trim()).filter(Boolean);
      }
      return [raw];
    },
    parseStudentPlanRates(planFee) {
      const parts = this.parseMultiValueField(planFee);
      if (!parts.length) return [0];
      return parts.map((fee) => parseFloat(String(fee).replace(/[^0-9.]/g, "")) || 0);
    },
    parseBundleParts(group) {
      const ids = this.parseMultiValueField(group?.plan_id);
      const rates = this.parseStudentPlanRates(group?.planFee);
      return ids.map((id, index) => ({
        id,
        monthlyRate: rates[index] ?? 0,
      }));
    },
    buildBundleEnrollmentBreakdown(group) {
      if (!group) return [];
      const planIds = this.parseMultiValueField(group.plan_id);
      if (planIds.length <= 1) return [];

      const monthlyParts = this.parseMultiValueField(group.monthly_premium);
      const proratedParts = this.parseMultiValueField(group.prorated_premium);
      const planFees = this.parseStudentPlanRates(group.planFee);

      return planIds.map((planId, index) => ({
        plan_id: planId,
        monthly_premium: monthlyParts[index] ?? monthlyParts[0] ?? "0.00",
        prorated_premium: proratedParts[index] ?? proratedParts[0] ?? "0.00",
        plan_fee: (planFees[index] ?? planFees[0] ?? 0).toFixed(2),
      }));
    },
    getStudentDiscountDecimal(plan) {
      if (plan.discount_rate != null && plan.discount_rate !== "") {
        return Number(plan.discount_rate) / 100;
      }
      if (plan.discount_rate_decimal != null && plan.discount_rate_decimal !== "") {
        return Number(plan.discount_rate_decimal) || 0;
      }
      return 0;
    },
    resolvePlanServiceFee(planGroup) {
      return resolveServiceFeeAmount(this.state, planGroup);
    },
    syncDisplayServiceFee() {
      const selected = this.state.bundled_group?.find((g) => g.select_plan);
      const fee = resolveServiceFeeAmount(this.state, selected || null);
      this.updateState({ key: "service_fee", value: fee.toFixed(2) });
    },
    handleStudentBillingPeriodChange(period) {
      if (this.state.student_billing_period === period) return;

      this.updateState({ key: "student_billing_period", value: period });
      const bundled_group = this.state.bundled_group.map((plan) => ({
        ...plan,
        select_plan: false,
      }));
      this.updateState({ key: "bundled_group", value: bundled_group });
      this.updateState({ key: "duplicate_value", value: false });
      this.checkPlan(this.state.payment_options);
      this.calculateStudentPlanAmounts();
      if (checkUsesStudentParameter(this.state)) {
        this.getEffDate();
      }
    },
    calculateStudentPlanAmounts() {
      const isAnnual = this.state.student_billing_period === "annual";
      const bundled_group = this.state.bundled_group;

      bundled_group.forEach((value_b) => {
        if (value_b.planFee != null && value_b.planFee !== "") {
          value_b.planFee = formatPlanPriceDisplay(value_b.planFee);
        }
        const serviceFee = this.resolvePlanServiceFee(value_b);
        if (checkUsesMixedStudentBundlePricing(this.state, value_b)) {
          this.applyMixedStudentBundleAmount(value_b, serviceFee, isAnnual);
          return;
        }

        const rates = this.parseStudentPlanRates(value_b.planFee);
        const discountDecimal = this.getStudentDiscountDecimal(value_b);
        const discountPercent =
          value_b.discount_rate != null && value_b.discount_rate !== ""
            ? Number(value_b.discount_rate)
            : discountDecimal * 100;
        const monthlyRate = rates.reduce((sum, rate) => sum + rate, 0);
        const yearlyPremiumGross = monthlyRate * 12;
        const discountAmount = yearlyPremiumGross * discountDecimal;

        let premium = 0;
        if (isAnnual) {
          premium = yearlyPremiumGross - discountAmount;
        } else {
          premium = monthlyRate;
        }

        value_b.student_premium_label = isAnnual ? "Yearly Premium" : "Per Month";
        value_b.student_rate_suffix = isAnnual ? "/year" : "/month";
        value_b.student_yearly_premium_gross = yearlyPremiumGross.toFixed(2);
        value_b.student_discount_amount = isAnnual ? discountAmount.toFixed(2) : "0.00";
        value_b.student_display_rate = isAnnual
          ? yearlyPremiumGross.toFixed(2)
          : premium.toFixed(2);
        value_b.student_net_premium = premium.toFixed(2);
        value_b.student_show_discount = isAnnual && discountPercent > 0;
        value_b.student_discount_percent = discountPercent;
        value_b.monthly_premium = premium.toFixed(2);
        value_b.prorated_premium = "0.00";
        value_b.total_prorated_prem = "0.00";
        value_b.total_monthly_prem = premium.toFixed(2);
        value_b.total = (premium + serviceFee).toFixed(2);
      });

      this.updateState({ key: "bundled_group", value: bundled_group });
      this.updateState({ key: "payment_frequency", value: isAnnual ? 1 : 12 });
      this.syncDisplayServiceFee();
    },
    applyGeneralPlanAmount(value_b, allSelectedPlans) {
      this.updateState({ key: "cdl_plan", value: false });
      const skipProrated = checkShouldSkipProrated(this.state);
      let planArr = [];

      if (value_b.planFee && typeof value_b.planFee == "string") {
        planArr = value_b.planFee.split(" + ").map(fee => parseFloat(fee).toFixed(2));
        value_b.planFee = planArr.join(" + ");
      } else if (value_b.planFee != null) {
        planArr = [parseFloat(value_b.planFee).toFixed(2)];
        value_b.planFee = planArr[0];
      }

      let planIdArr = [];
      if (value_b.plan_id && typeof value_b.plan_id == "string") {
        planIdArr = value_b.plan_id.split(" + ");
      } else if (value_b.plan_id != null) {
        planIdArr = [String(value_b.plan_id)];
      }

      let total_monthly_prem = 0;
      let total_prorated_prem = 0;
      let monthlyPremiumArr = [];
      let proratedPremiumArr = [];

      planArr.forEach((valuePrc) => {
        valuePrc = ((parseFloat(valuePrc) * 12) / parseFloat(this.state.payment_frequency)).toFixed(2);

        let monthlyPremium = parseFloat(valuePrc);
        total_monthly_prem += parseFloat(monthlyPremium);
        let proratedPremium = skipProrated
          ? 0
          : (parseFloat(monthlyPremium) / this.state.last_day_month) *
            this.state.day_diff;
        total_prorated_prem += parseFloat(proratedPremium);

        proratedPremiumArr.push(skipProrated ? "0.00" : proratedPremium.toFixed(2));
        monthlyPremiumArr.push(monthlyPremium.toFixed(2));
      });
      value_b.total_monthly_prem = total_monthly_prem.toFixed(2);
      value_b.total_prorated_prem = skipProrated ? "0.00" : total_prorated_prem.toFixed(2);

      if (this.state.payment_method == "CC") {
        const planServiceFee = this.resolvePlanServiceFee(value_b);
        value_b.total = skipProrated
          ? parseFloat(total_monthly_prem) + planServiceFee
          : (
          parseFloat(total_monthly_prem) +
          parseFloat(total_prorated_prem) +
          planServiceFee
        );
      } else {
        value_b.total = total_monthly_prem;
      }
      value_b.total = parseFloat(value_b.total).toFixed(2);
      planIdArr.forEach((valueId) => {
        allSelectedPlans.push(valueId);
      });

      value_b.prorated_premium = skipProrated ? "0.00" : proratedPremiumArr.join(" + ");
      value_b.monthly_premium = monthlyPremiumArr.join(" + ");
      value_b.student_show_discount = false;
      value_b.student_discount_percent = 0;
      value_b.student_discount_amount = "0.00";
    },
    applyMixedStudentBundleAmount(value_b, serviceFee, isAnnual) {
      const parts = this.parseBundleParts(value_b);
      const discountDecimal = this.getStudentDiscountDecimal(value_b);
      const discountPercent =
        value_b.discount_rate != null && value_b.discount_rate !== ""
          ? Number(value_b.discount_rate)
          : discountDecimal * 100;

      let monthlyTotal = 0;
      let yearlyPremiumGross = 0;
      let discountAmount = 0;
      let yearlyNet = 0;

      parts.forEach(({ monthlyRate }) => {
        monthlyTotal += monthlyRate;
        const yearlyPart = monthlyRate * 12;
        yearlyPremiumGross += yearlyPart;
        const partDiscount = yearlyPart * discountDecimal;
        discountAmount += partDiscount;
        yearlyNet += yearlyPart - partDiscount;
      });

      const premium = isAnnual ? yearlyNet : monthlyTotal;

      value_b.student_premium_label = isAnnual ? "Yearly Premium" : "Per Month";
      value_b.student_rate_suffix = isAnnual ? "/year" : "/month";
      value_b.student_yearly_premium_gross = yearlyPremiumGross.toFixed(2);
      value_b.student_discount_amount = isAnnual ? discountAmount.toFixed(2) : "0.00";
      value_b.student_display_rate = isAnnual
        ? yearlyPremiumGross.toFixed(2)
        : monthlyTotal.toFixed(2);
      value_b.student_net_premium = premium.toFixed(2);
      value_b.student_show_discount = isAnnual && discountAmount > 0;
      value_b.student_discount_percent = discountPercent;
      value_b.monthly_premium = premium.toFixed(2);
      value_b.prorated_premium = "0.00";
      value_b.total_prorated_prem = "0.00";
      value_b.total_monthly_prem = premium.toFixed(2);
      value_b.total = (premium + serviceFee).toFixed(2);
    },
    applyStudentPlanAmount(value_b, serviceFee, isAnnual) {
      const rates = this.parseStudentPlanRates(value_b.planFee);
      const discountDecimal = this.getStudentDiscountDecimal(value_b);
      const discountPercent =
        value_b.discount_rate != null && value_b.discount_rate !== ""
          ? Number(value_b.discount_rate)
          : discountDecimal * 100;
      const monthlyRate = rates.reduce((sum, rate) => sum + rate, 0);
      const yearlyPremiumGross = monthlyRate * 12;
      const discountAmount = yearlyPremiumGross * discountDecimal;

      let premium = 0;
      if (isAnnual) {
        premium = yearlyPremiumGross - discountAmount;
      } else {
        premium = monthlyRate;
      }

      value_b.student_premium_label = isAnnual ? "Yearly Premium" : "Per Month";
      value_b.student_rate_suffix = isAnnual ? "/year" : "/month";
      value_b.student_yearly_premium_gross = yearlyPremiumGross.toFixed(2);
      value_b.student_discount_amount = isAnnual ? discountAmount.toFixed(2) : "0.00";
      value_b.student_display_rate = isAnnual
        ? yearlyPremiumGross.toFixed(2)
        : premium.toFixed(2);
      value_b.student_net_premium = premium.toFixed(2);
      value_b.student_show_discount = isAnnual && discountPercent > 0;
      value_b.student_discount_percent = discountPercent;
      value_b.monthly_premium = premium.toFixed(2);
      value_b.prorated_premium = "0.00";
      value_b.total_prorated_prem = "0.00";
      value_b.total_monthly_prem = premium.toFixed(2);
      value_b.total = (premium + serviceFee).toFixed(2);
    },
    calculateMixedPlanAmounts() {
      const isAnnual = this.state.student_billing_period === "annual";
      const bundled_group = this.state.bundled_group;
      const allSelectedPlans = [];

      bundled_group.forEach((value_b) => {
        if (value_b.planFee != null && value_b.planFee !== "") {
          value_b.planFee = formatPlanPriceDisplay(value_b.planFee);
        }
        const serviceFee = this.resolvePlanServiceFee(value_b);
        if (checkUsesMixedStudentBundlePricing(this.state, value_b)) {
          this.applyMixedStudentBundleAmount(value_b, serviceFee, isAnnual);
        } else if (checkStudentPlanGroup(value_b)) {
          this.applyStudentPlanAmount(value_b, serviceFee, isAnnual);
        } else {
          this.applyGeneralPlanAmount(value_b, allSelectedPlans);
        }
      });

      this.updateState({ key: "bundled_group", value: bundled_group });
      this.updateState({ key: "payment_frequency", value: isAnnual ? 1 : 12 });
      const checkDupl = this.checkDupes(allSelectedPlans);
      this.updateState({ key: "duplicate_value", value: checkDupl });
      this.syncDisplayServiceFee();
    },
    calculatePlanAmounts() {
      if (checkShouldUseStudentPricing(this.state)) {
        if (this.state.planInterest === "All") {
          this.calculateMixedPlanAmounts();
        } else {
          this.calculateStudentPlanAmounts();
        }
        return;
      }
      let allSelectedPlans = [];
      let bundled_group = this.state.bundled_group;
      const skipProrated = checkShouldSkipProrated(this.state);

      bundled_group.forEach((value_b) => {
        this.updateState({ key: "cdl_plan", value: false });
        let planArr = [];

        if (value_b.planFee && typeof value_b.planFee == "string") {
          planArr = value_b.planFee.split(" + ").map(fee => parseFloat(fee).toFixed(2));
          value_b.planFee = planArr.join(" + ");
        } else if (value_b.planFee != null) {
          planArr = [parseFloat(value_b.planFee).toFixed(2)];
          value_b.planFee = planArr[0];
        }

        let planIdArr = [];
        if (value_b.plan_id && typeof value_b.plan_id == "string") {
          planIdArr = value_b.plan_id.split(" + ");
        } else if (value_b.plan_id != null) {
          planIdArr = [String(value_b.plan_id)];
        }

        let total_monthly_prem = 0;
        let total_prorated_prem = 0;
        let monthlyPremiumArr = [];
        let proratedPremiumArr = [];

        planArr.forEach((valuePrc) => {
          valuePrc = ((parseFloat(valuePrc) * 12) / parseFloat(this.state.payment_frequency)).toFixed(2);

          let monthlyPremium = parseFloat(valuePrc);
          total_monthly_prem += parseFloat(monthlyPremium)
          let proratedPremium = skipProrated
            ? 0
            : (parseFloat(monthlyPremium) / this.state.last_day_month) *
            this.state.day_diff
          total_prorated_prem += parseFloat(proratedPremium);

          proratedPremiumArr.push(skipProrated ? "0.00" : proratedPremium.toFixed(2));
          monthlyPremiumArr.push(monthlyPremium.toFixed(2));
        });
        value_b.total_monthly_prem = total_monthly_prem.toFixed(2);
        value_b.total_prorated_prem = skipProrated ? "0.00" : total_prorated_prem.toFixed(2);

        if (this.state.payment_method == 'CC') {
          const planServiceFee = this.resolvePlanServiceFee(value_b);
          value_b.total = skipProrated
            ? parseFloat(total_monthly_prem) + planServiceFee
            : (
            parseFloat(total_monthly_prem) +
            parseFloat(total_prorated_prem) +
            planServiceFee
          )
        } else {
          value_b.total = total_monthly_prem;
        }
        value_b.total = parseFloat(value_b.total).toFixed(2);
        planIdArr.forEach((valueId) => {
          allSelectedPlans.push(valueId);
        });

        value_b.prorated_premium = skipProrated ? "0.00" : proratedPremiumArr.join(" + ");
        value_b.monthly_premium = monthlyPremiumArr.join(" + ");
        value_b.student_show_discount = false;
        value_b.student_discount_percent = 0;
        value_b.student_discount_amount = "0.00";
      });

      this.updateState({ key: "bundled_group", value: bundled_group });
      let checkDupl = this.checkDupes(allSelectedPlans);
      this.updateState({ key: "duplicate_value", value: checkDupl });
      this.syncDisplayServiceFee();
    },
    setChoice(q, c) {
      let b_group = q;
      b_group.forEach((plan) => {
        plan.select_plan = false;
        if (plan.plan_id == c.plan_id) {
          plan.select_plan = true;
        }
      });
      this.checkPlan(this.state.payment_options);
      this.updateState({ key: "bundled_group", value: b_group });
      this.calculatePlanAmounts();
      this.checkPlan(this.state.payment_options);
    },
    reloadPlan(enrollOption) {
      if (this.state.disp_enroll_section) {
        this.getPlan(enrollOption);
      }
    },
    async createMember(callback) {
      this.trackGeneralEnrollment(GENERAL_ENROLLMENT_EVENTS.SUBMIT);
      this.isSubmitting = true;
      this.deepTrim(this.state.member);
      this.deepTrim(this.localForm);
      this.deepTrim(this.form);
      this.v$.$touch();
      // Check 1: Honeypot
      if (this.hp !== "") {
        this.isSubmitting = false;
        callback?.(null);
        Swal.fire({
          title: "Submission Blocked",
          text: "We could not process your request. Please refresh the page and try again.",
          icon: "warning",
          confirmButtonText: "OK",
        });
        return;
      }
      // Check 2: Timing (anti-bot: ignore submits in first 3s after page load)
      if (Date.now() - this.startTime < 3000) {
        this.isSubmitting = false;
        callback?.(null);
        Swal.fire({
          title: "Please Wait",
          text: "Please wait a few seconds and try again.",
          icon: "info",
          confirmButtonText: "OK",
        });
        return;
      }
      if (!this.v$.$invalid) {
        const selectedPlans = filter(this.state.bundled_group, { select_plan: true });
        if (selectedPlans.length === 0) {
          this.isSubmitting = false;
          callback?.(null);
          swal.fire({
            title: "We're Sorry!",
            text: "Please select a plan before submitting.",
            icon: "warning",
            confirmButtonText: "OK",
            confirmButtonAriaLabel: "OK",
          });
          return false;
        }
        const phoneNumberArray = ["(000)000-0000", "(123)456-7890"];
        const checkPhoneNumber = phoneNumberArray.includes(
          { ...this.state.member.phone }
        );
        if (checkPhoneNumber) {
          this.isSubmitting = false;
          callback?.(null);
          swal.fire({
            title: "We're Sorry!",
            text: "Please enter valid phone",
            icon: "warning",
            confirmButtonText: "OK",
            confirmButtonAriaLabel: "OK",
          });
          return false;
        }
        let member_details = { ...this.state.member };
        member_details.card_holder_cell = String(
          this.user_form.card_holder_cell ?? member_details.card_holder_cell ?? "false"
        );
        member_details.address1 = String(
          member_details.address1 || member_details.address || ""
        ).trim();
        console.log(member_details.date_of_birth)
        const dob = member_details.date_of_birth;
        console.log(dob)

        const parsedDate = moment(dob, "MM/DD/YYYY");
        const month = parsedDate.month() + 1; // Month (1 to 12)
        const day = parsedDate.date(); // Day (1 to 31)

        if (month > 12 || day > 31 || isNaN(month) || isNaN(day)) {
          this.isSubmitting = false;
          callback?.(null);
          swal.fire({
            title: "Error",
            text: "Invalid date. Please enter a correct date.",
            icon: "warning",
            confirmButtonText: "OK",
            confirmButtonAriaLabel: "OK",
          });
          return false;
        }
        const years = moment().diff(dob, "years");
        console.log(years)
        if (years < 17) {
          this.isSubmitting = false;
          callback?.(null);
          swal.fire({
            title: "Error",
            text: "The person will be at least 17 years old",
            icon: "warning",
            confirmButtonText: "OK",
            confirmButtonAriaLabel: "OK",
          });
          return false; // Return false to prevent further form processing
        }
        if (this.state.duplicate_value) {
          this.isSubmitting = false;
          callback?.(null);
          swal.fire({
            title: "Error",
            text: "Plancode should not be duplicates.",
            icon: "warning",
            confirmButtonText: "OK",
            confirmButtonAriaLabel: "OK",
          });
          return false; // Prevent further form processing
        }

        // Check if SSN is required and validate SSN input
        if (this.state.ssn_required == "1") {
          const ssnWithoutDash = String(member_details.ssn).replace("-", "");
          if (!member_details.ssn || ssnWithoutDash.length !== 10) {
            this.isSubmitting = false;
            callback?.(null);
            swal.fire({
              title: "Error",
              text: "Please enter valid SSN number.",
              icon: "warning",
              confirmButtonText: "OK",
              confirmButtonAriaLabel: "OK",
            });
            return false; // Prevent further form processing
          }
        }

        // if (
        //   (this.state.company_number === "9001204" &&
        //     this.state.opt_type === "direct_page") || this.state.opt_type == 'mdsid'
        // ) {
        //   member_details.occupation =
        //     member_details.occupation === "other"
        //       ? member_details.other_occupation
        //       : member_details.occupation;
        // }
        // Handle different enrollment options
        console.log(this.state.company);
        // if (!window.grecaptcha) return;
        // const token = await window.grecaptcha.execute(
        //   this.siteKey,
        //   { action: 'payment' }
        // );
        // return false;
        // Keep legacy precedence used by submit flow.
        member_details.company_id =
          this.state.company?.company_id ??
          this.state.company?.id ??
          this.state.company_number ??
          this.searchObject?.opt;
        member_details.company_emails =
          this.state.company_emails || this.state.company?.email;

        // Assuming bundledGroup is an array of selected plans
        const selectedData = this.state.bundled_group.filter(
          (g) =>
            g.select_plan === true ||
            g.select_plan === 1 ||
            g.select_plan === "1"
        );
        console.log(selectedData);
        // return false
        if (member_details.exp_month != "" && member_details.exp_month != undefined) {
          const [month, year] = member_details.exp_month.split("/");
          member_details.exp_month = month
          member_details.exp_year = year;
        }
        if (member_details.cc != "" && member_details.cc != undefined) {
          member_details.cc = member_details.cc.replace(/-/g, "");
        }
        // Selected bundle only — include per-plan breakdown for multi-plan products
        member_details.bundled_group = selectedData;
        if (selectedData[0]) {
          const planIds = this.parseMultiValueField(selectedData[0].plan_id);
          member_details.plan_id = planIds.join(",");
          member_details.bundle_plan_breakdown = this.buildBundleEnrollmentBreakdown(selectedData[0]);
          member_details.associate_group = selectedData[0].assoc_grp;
        }
        member_details.plan_state = this.state.company.state;
        member_details.plan_type = selectedData[0]?.description || "";
        member_details.amount = selectedData[0]?.total;
        member_details.prorated_premium = selectedData[0]?.total_prorated_prem;
        member_details.monthly_premium = selectedData[0]?.total_monthly_prem;
        member_details.effective_date = moment(this.state.eff_date).format("YYYY-MM-DD");
        member_details.next_recurring_date = this.state.next_recurring_date
          ? moment(this.state.next_recurring_date).format("YYYY-MM-DD")
          : "";
        member_details.date_of_birth = moment(member_details.date_of_birth).format("YYYY-MM-DD");
        member_details.referer_url = this.state.referer_url;
        member_details.disclaimer_text = this.state.enrollment_settings.disclaimer_text;
        member_details.e_sign_text = this.state.enrollment_settings.e_sign_text;
        member_details.send_enrollment_pdf = this.state.enrollment_settings.send_enrollment_pdf;
        member_details.payment_frequency = this.state.payment_frequency;
        // member_details.g_token=token;
        try {
          if (this.state.payroll_enrollment == '1') {
            member_details.memberType = 'enrollment';
          } else {
            member_details.memberType = 'member';
          }
          // Dispatch the `createMember` action, passing the payload
          const selectedStudentPlan = this.state.bundled_group.find((g) => g.select_plan);
          let response = await this.$store.dispatch("createMember", {
            enroll_option: this.state.enroll_option,
            payment_method: this.state.payment_method,
            opt_type: this.effectiveOptType,
            payroll_enrollment: this.state.payroll_enrollment,
            student_school_state: this.state.member?.state || this.state.student_school_state,
            student_university_name: (this.state.member?.school_name || this.state.student_university_name || "").trim(),
            student_billing_period: this.state.student_billing_period,
            student_discount_amount:
              checkUsesStudentPlanPricing(this.state, selectedStudentPlan) ||
              checkUsesMixedStudentBundlePricing(this.state, selectedStudentPlan)
                ? selectedStudentPlan?.student_discount_amount || "0.00"
                : "0.00",
            ...member_details,
            processing_fee: resolveServiceFeeAmount(
              this.state,
              selectedStudentPlan
            ).toFixed(2),
          });

          if (response.data.status == 'success') {
            pushEnrollmentSuccessEvent(this.state, {
              selectedPlan: selectedData[0] || null,
              memberDetails: member_details,
              response,
              serviceFee: resolveServiceFeeAmount(
                this.state,
                selectedStudentPlan || selectedData[0] || null
              ).toFixed(2),
              discount: selectedStudentPlan?.student_discount_amount || "0.00",
            });
            this.isSubmitting = false;
            callback(response);
            this.resetForm();
            swal.fire({
              title: "Success!",
              text: "Thank you for your enrollment! Once your enrollment is activated, you will receive an email with your membership details.",
              icon: "success",
              confirmButtonText: "OK",
              confirmButtonAriaLabel: "OK",
            });
            this.updateState({ key: "disp_enroll_section", value: false });

          } else {
            this.trackGeneralEnrollment(GENERAL_ENROLLMENT_EVENTS.FAILED, {
              error_message: response.data.message || "Enrollment failed",
            });
            this.isSubmitting = false;
            callback(null)
            swal.fire({
              title: "We're Sorry!",
              text: response.data.message,
              icon: "error",
              confirmButtonText: "OK",
              confirmButtonAriaLabel: "OK",
            });
          }
        } catch (error) {
          this.trackGeneralEnrollment(GENERAL_ENROLLMENT_EVENTS.FAILED, {
            error_message: error.message || "Unexpected enrollment error",
          });
          this.isSubmitting = false;
          callback(null)
          swal.fire({
            title: "We're Sorry!",
            text:
              error.message ||
              "An unexpected error occurred during member creation.",
            icon: "error",
            confirmButtonText: "OK",
            confirmButtonAriaLabel: "OK",
          });
        }
      } else {
        this.isSubmitting = false;
        callback(null)
        swal.fire({
          title: "We're Sorry!",
          text: "Enter required fields!",
          icon: "error",
          confirmButtonText: "OK",
          confirmButtonAriaLabel: "OK",
        });
      }
    },
  },
};
</script>
